/**
 * All UI strings for the booking widget.
 * Add new keys here in both languages — never add inline ternaries in components.
 *
 * Usage:
 *   import { t } from '../i18n/translations';
 *   t(lang, 'findSessions')
 *   t(lang, 'minutes', { count: 45 })   // → "45 хвилин" / "45 minutes"
 */

const translations = {
    ua: {
        // Language selector
        language: "Мова",

        // Sentinel names (used when renaming "any" / "no" entries from the API)
        anyBoat:   "Будь-який катер",
        anyCoach:  "Будь-який тренер",
        noCoach:   "Без тренера",

        // Form labels
        boat:        "Катер",
        coach:       "Тренер",
        totalRiders: "Всього райдерів",
        rookies:     "З них початківців",
        name:        "Імʼя",
        phone:       "Телефон",
        phoneFormat: "В форматі +3800000000000",
        bookingComment: "Коментар до бронювання (необовʼязково)",

        // Tooltip texts for form fields
        boatTooltip:     "В нашому клубі є катери різних моделей, обери свій улюблений. Дізнатись більше про катери можна на сайті",
        coachTooltip:    "Обери свого тренера для ефективного тренування. Якщо ти тренуєшся вперше - заняття проходить за участю тренера, це обов'язково і повір - продуктивно та безпечно ;)",
        ridersTooltip:   "Вкажіть кількість людей що будуть катати",
        beginnersTooltip: "Вкажіть кількість райдерів, що будуть вперше займатись вейсерфом. Це дуже допоможе тренеру підготуватись до тренування, розрахувати час та провести інструктаж. ",

        // Field status messages
        gettingCoaches:    "Список доступних тренерів завантажується",
        noCoachesAvailable: "Упс! Схоже в цей час вже немає вільного тренера ;( Давай спробуємо обрати інший день.",
        gettingBoats:      "Список доступних катерів завантажується",

        // Slot selection
        availableSlots: "Доступні слоти (оберіть підряд)",
        slotsHintLine1: "Щоб обрати довший час, натисніть кілька сусідніх слотів.",
        slotsHintLine2: "Перевірте загальну тривалість у підсумку бронювання.",
        noAvailableSlots: "Немає доступних слотів",
        confirmSlots: "Готово",

        // Session search
        findSessions:       "Знайти сесії",
        sessionsFound:      "Сесії знайдено.",
        noSessionsFound:    "По заданим параметрам немає вільної сесії",
        sessionSearchError: "Сталася помилка при пошуку сесій",
        lookingForSession:  "Шукаємо сесію...",
        sessionNotFound:    "Сесія не знайдена для цих параметрів",
        sessionCheckError:  "Помилка при перевірці сесії",

        // Contact / account
        contactDetails:  "Ваші контактні дані ",
        accountFound:    "Обліковий запис знайдено",

        // Booking flow buttons
        goToBooking: "Перейти до бронювання",
        book:        "Забронювати",

        // Coach required for beginners
        coachRequiredForBeginners: "Будь ласка, оберіть тренера для початківців",
        coachAlertMessage: "Упс! Якщо ви тренуєтесь вперше - необхідно обрати тренера. Повірте, так буде краще!",

        // Summary panel — idle state
        summaryIdleHint: "Оберіть доступні слоти та натисніть 'Перейти до бронювання' для підтвердження.",

        // Summary panel — booking details
        bookingDetails: "Деталі бронювання ",
        start:          "Початок:",
        end:            "Кінець:",
        date:           "Дата",
        totalDuration:  "Загальна тривалість сесії (хв)",
        minutes:        "хвилин",
        balance:        "Залишок на рахунку",
        uah:            "грн",
        chooseStartForPrice: "Для остаточної ціни оберіть початок сесії",
        boatPrice:      "Вартість катера",
        coachPrice:     "Вартість тренера",
        totalPrice:     "Повна вартість",
        paymentType:    "Формат оплати",
        fullPayment:    "Повна оплата",
        prepayment50:   "Передоплата 50%",
        toPay:          "До сплати",

        // Sync warning
        selectionsChanged: "Увага — параметри змінились. Застосуйте зміни через «Перейти до бронювання» або поверніть їх, щоб відповідали відображеним деталям.",

        // Cancellation policy
        cancellationPolicyTitle: "Правила скасування тренування",
        cancellationPolicy1: "За 24 години та більше, без штрафних санкцій.",
        cancellationPolicy2: "За 24 години та менше, якщо знаходимо заміну - штраф не стягується. У випадку якщо не знаходимо - стягується 50% від заявленого часу катання.",
        cancellationPolicy3: "Неявка на тренування без попередження - гроші не повертаються.",

        // Checkout errors
        checkoutError: "Сталася помилка при формуванні чекауту",

        // Invoice destination (payment description sent to Monobank)
        invoiceBooked:    "Оплата броні на",
        invoiceSessionStart: "Початок сесії:",
        invoiceDuration:  "загальна тривалість сесії:",
        invoiceRiders:    "райдерів",

        // Success / error dialogs
        successTitle: "Сесія успішно заброньована та оплачена",
        successBody:  "Дякуємо за покупку!",
        errorTitle:   "Щось пішло не так",
        errorBody:    "Будь ласка, спробуйте ще раз пізніше",

        // Checkout go-back
        goBack: "Повернутися до вибору",

        // Contact not found (user hasn't shared phone via bot)
        contactNotFoundTitle:   "Поділіться номером телефону",
        contactNotFoundMessage: "Щоб продовжити бронювання, спочатку поділіться своїм контактом через бота.",
        openBot: "Відкрити бота",
    },

    en: {
        language: "Language",

        anyBoat:  "Any boat",
        anyCoach: "Any coach",
        noCoach:  "Without coach",

        boat:        "Boat",
        coach:       "Coach",
        totalRiders: "Total riders",
        rookies:     "Rookies among them",
        name:        "Name",
        phone:       "Phone",
        phoneFormat: "Format +3800000000000",
        bookingComment: "Booking comment (optional)",

        boatTooltip:     "Our club has boats of different models, choose your favorite. You can learn more about the boats on the website",
        coachTooltip:    "Choose your coach for an effective training. If you are training for the first time - the lesson is held with the participation of a trainer, it is mandatory and believe me - productive and safe ;)",
        ridersTooltip:   "Specify the number of people who will ride",
        beginnersTooltip: "Indicate the number of riders who will be going wakesurfing for the first time. This will help the coach to prepare for the training, calculate the time and give instructions. ",

        gettingCoaches:    "The list of available coaches is loading",
        noCoachesAvailable: "Oops! It looks like there's no coach available at this time ;( Let's try to choose another day.",
        gettingBoats:      "The list of available boats is loading",

        availableSlots:   "Available slots (select consecutive)",
        slotsHintLine1:   "To select a longer duration, click on several consecutive slots.",
        slotsHintLine2:   "Check the total duration in the booking summary.",
        noAvailableSlots: "No available slots",
        confirmSlots: "Done",

        findSessions:       "Find sessions",
        sessionsFound:      "Sessions found.",
        noSessionsFound:    "There are no available sessions for these parameters.",
        sessionSearchError: "An error occurred while searching for sessions.",
        lookingForSession:  "Looking for a session...",
        sessionNotFound:    "Session not found for these parameters",
        sessionCheckError:  "Error checking session",

        contactDetails: "Your contact details",
        accountFound:   "Account found",

        goToBooking: "Go to booking",
        book:        "Book",

        coachRequiredForBeginners: "Please select a coach for beginners",
        coachAlertMessage: "Oops! If you're training for the first time, you need to choose a coach. Trust us, it will be better!",

        summaryIdleHint: "Pick available slots and click 'Go to booking' to proceed.",

        bookingDetails: "Booking details",
        start:          "Start:",
        end:            "End:",
        date:           "Date",
        totalDuration:  "Total session duration (min)",
        minutes:        "minutes",
        balance:        "Balance",
        uah:            "UAH",
        chooseStartForPrice: "Choose a session start for a final price",
        boatPrice:      "Boat price",
        coachPrice:     "Coach price",
        totalPrice:     "Total price",
        paymentType:    "Payment type",
        fullPayment:    "Full payment",
        prepayment50:   "50% prepayment",
        toPay:          "To pay",

        selectionsChanged: "Warning — selections changed. Apply changes with \"Go to booking\" or revert to match the displayed booking.",

        cancellationPolicyTitle: "Training Cancellation Policy",
        cancellationPolicy1: "24 hours or more in advance – cancellation is free of charge.",
        cancellationPolicy2: "Less than 24 hours in advance – if we find a replacement, no fee is charged. If no replacement is found, a 50% fee of the booked training time applies.",
        cancellationPolicy3: "No-shows without prior notice – no refund is provided.",

        checkoutError: "An error occurred while generating the checkout link.",

        invoiceBooked:       "Booked for",
        invoiceSessionStart: "Session start",
        invoiceDuration:     "total session duration:",
        invoiceRiders:       "riders",

        successTitle: "Session was successfully booked and paid",
        successBody:  "Thank you for your purchase!",
        errorTitle:   "Something went wrong",
        errorBody:    "Please, try again later",

        goBack: "Go back to options",

        // Contact not found (user hasn't shared phone via bot)
        contactNotFoundTitle:   "Share your phone number",
        contactNotFoundMessage: "To proceed with booking, please share your contact via the bot first.",
        openBot: "Open bot",
    },
};

/**
 * Translate a key for the given language.
 * Falls back to English, then to the key itself if missing in both.
 *
 * @param {'ua'|'en'} lang
 * @param {string} key
 * @returns {string}
 */
export function t(lang, key) {
    return translations[lang]?.[key] ?? translations['en'][key] ?? key;
}

export default translations;
